<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
      <span>
        Price Conversions
      </span>
      <div
        class="d-flex flex-row align-center"
        style="gap: 0.625rem;">
        <v-btn
          v-if="isEdit"
          color="error"
          outlined
          rounded
          small
          :loading="loading.contentLoading"
          :disabled="loading.mainLoading"
          @click="onReset()">
          {{ $t('button.Reset') }}
        </v-btn>
        <v-btn
          color="success"
          rounded
          small
          :loading="loading.contentLoading"
          :disabled="loading.mainLoading || !isEdit"
          @click="updateAllItems()">
          {{ $t('button.Save') }}
        </v-btn>
        <v-btn
          color="secondary"
          rounded
          small
          :loading="loading.contentLoading"
          :disabled="loading.mainLoading || query.country === null"
          @click="onAddItem()">
          {{ $t('button.Add') }}
        </v-btn>
      </div>
    </div>

    <v-row class="px-4">
      <v-col
        cols="12"
        md="4">
        <div class="card-container elevation-3">
          <v-row>
            <v-col
              cols="12"
              class="card-title">
              Country to 🗺️ "DO"
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="query.country"
                placeholder="Country"
                color="secondary"
                prepend-inner-icon="mdi-flag-variant-outline"
                :items="shippingCountries"
                item-text="country"
                item-value="code"
                hide-details
                :loading="loading.mainLoading" />
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        v-show="query.country"
        cols="12"
        md="8">
        <div class="card-container elevation-3">
          <v-row>
            <v-col cols="12">
              <div class="d-flex flex-row align-center justify-space-between">
                <span class="card-title">
                  Table for 💵 "MAPPING"
                </span>
              </div>
            </v-col>
            <v-col cols="12">
              <v-form
                ref="form"
                @submit.prevent="updateAllItems()">
                <v-data-table
                  :headers="headers"
                  :items="items"
                  :items-per-page="-1"
                  :loading="loading.mainLoading || loading.contentLoading"
                  hide-default-footer>
                  <template #[`item.thb`]="{ index, item }">
                    <v-text-field
                      :value="showPriceFormat(item.thb)"
                      :rules="numberRules"
                      :readonly="loading.mainLoading || loading.contentLoading"
                      :disabled="Boolean(item.id)"
                      suffix="฿"
                      placeholder="THB"
                      color="secondary"
                      dense
                      hide-details
                      @input="onTHBInput($event, index)" />
                  </template>
                  <template #[`item.value`]="{ index, item }">
                    <v-text-field
                      :value="showPriceFormat(item.value)"
                      :rules="numberRules"
                      :readonly="loading.mainLoading || loading.contentLoading"
                      suffix="$"
                      placeholder="Converted Value"
                      color="secondary"
                      dense
                      hide-details
                      @input="onValueInput($event, index)" />
                  </template>
                  <template #[`item.action`]="{ index }">
                    <div class="d-flex flex-row justify-center align-center">
                      <v-btn
                        color="error"
                        small
                        icon
                        @click="removeItem(index)">
                        <v-icon small>
                          mdi-delete
                        </v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-form>
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        v-if="!query.country"
        cols="12"
        md="8">
        <div class="card-container elevation-3">
          <v-row>
            <v-col cols="12">
              <div class="warning-box">
                <span>
                  Please select
                </span>
                <span class="font-weight-bold">
                  "COUNTRY"
                </span>
                <span>
                  to show products
                </span>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { getRole } from '@/assets/js/Authentication'
import ExportCurrencyRatesProvider from '@/resources/ExportCurrencyRatesProvider'
import ExportPriceConversionsProvider from '@/resources/ExportPriceConversionsProvider'
import ShippingCountries from '@/assets/json/ShippingCountries.json'

const ExportCurrencyRatesService = new ExportCurrencyRatesProvider()
const ExportPriceConversionsService = new ExportPriceConversionsProvider()

const isNumber = (text) => {
  const tmpText = text.replaceAll(',', '')

  return /^[+-]?(?:\d+\.?|\d*\.\d+)$/.test(tmpText)
}

const parseNumber = (text) => {
  const tmpText = text.replaceAll(',', '')

  if (isNumber(text)) {
    return Number(tmpText)
  }

  return 0
}

export default {
  data () {
    return {
      loading: {
        mainLoading: true,
        contentLoading: false
      },
      headers: [
        {
          text: 'THB',
          value: 'thb',
          sortable: false,
          align: 'center'
        },
        {
          text: 'ราคา',
          value: 'value',
          sortable: false,
          align: 'center'
        },
        {
          text: 'ตัวเลือก',
          value: 'action',
          sortable: false,
          align: 'center'
        }
      ],
      memoItems: [],
      items: [],
      shippingCountries: [],
      query: {
        sortBy: 'thb',
        sortOrder: 'desc',
        country: null
      },
      requiredRules: [
        (v) => !!v || 'โปรดกรอก Field นี้'
      ],
      whiteSpaceRules: [
        (v) => !v?.toString()?.includes(' ') || 'ห้ามมีช่องว่างใน Field นี้'
      ],
      numberRules: [
        (v) => isNumber(v) || 'This field is invalid.',
        (v) => parseNumber(v) >= 0 || 'This field must be positive number.'
      ]
    }
  },
  computed: {
    isDeveloper () {
      return getRole() === 'developer'
    },
    isEdit () {
      return JSON.stringify(this.items) !== JSON.stringify(this.memoItems)
    },
    currency () {
      return this.shippingCountries.find((c) => c.code === this.query.country)?.currency || ''
    }
  },
  watch: {
    'query.sortBy': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    'query.sortOrder': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    'query.country': {
      handler () {
        this.getItems(true)
      },
      deep: true
    }
  },
  mounted () {
    this.initialize()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),
    getShippingCountry (code) {
      return ShippingCountries.find((country) => country.code === code)?.country || code
    },
    onAddItem () {
      this.items.push({
        currency: this.currency,
        value: null,
        thb: null,
        country: this.query.country
      })
    },
    async initialize () {
      try {
        this.loading.mainLoading = true

        await this.getCountries()
      } catch (error) {
        console.error('initialize', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[INITIALIZE-ERROR]: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading.mainLoading = false
      }
    },
    async getCountries () {
      try {
        const { data } = await ExportCurrencyRatesService.getItems({})

        this.shippingCountries = data.map((r) => ({
          country: this.getShippingCountry(r.country),
          code: r.country,
          currency: r.currency
        }))
      } catch (error) {
        console.error('getCountries', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `[GET-COUNTRIES-ERROR]: ${error.message}`,
          type: 'error'
        })
      }
    },
    async getItems (isClear = false) {
      if (this.query.country === null) {
        return
      }

      try {
        this.loading.contentLoading = true

        this.headers = [
          {
            text: 'THB',
            value: 'thb',
            sortable: false,
            align: 'center'
          },
          {
            text: this.currency || this.query.country,
            value: 'value',
            sortable: false,
            align: 'center'
          },
          {
            text: 'ตัวเลือก',
            value: 'action',
            sortable: false,
            align: 'center'
          }
        ]

        const { data } = await ExportPriceConversionsService.getItems({
          sortBy: this.query.sortBy,
          sortOrder: this.query.sortOrder,
          country: this.query.country
        })

        const mappedData = data.map((v) => ({
          ...v,
          name: v?.name?.id || v?.name
        }))

        if (isClear) {
          this.memoItems = []
          this.items = []
        }

        this.memoItems = JSON.parse(JSON.stringify(mappedData))
        this.items = mappedData
      } catch (error) {
        console.error('getItems', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading.contentLoading = false
      }
    },
    confirmUpdateItem (item) {
      this.setModal({
        value: true,
        title: 'Update Item',
        message: `Do you want to update ${this.getShippingCountry(item.country)}?`,
        confirmText: 'Update',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.updateItem(item.id)
      })
    },
    confirmUpdateAllItems () {
      this.setModal({
        value: true,
        title: 'Update Item',
        message: 'Do you want to update all items?',
        confirmText: 'Update',
        confirmType: 'success',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.updateAllItems()
      })
    },
    removeItem (index) {
      this.items.splice(index, 1)
    },
    onReset () {
      this.items = JSON.parse(JSON.stringify(this.memoItems))
    },
    async updateAllItems () {
      try {
        this.loading.contentLoading = true

        const isValid = await this.$refs.form.validate()

        if (!isValid) {
          return
        }

        const toBeInActive = this.memoItems.filter((item) => !this.items.some((i) => i.id === item.id))
        const mappedItems = []

        for (const [index, item] of this.items.entries()) {
          mappedItems.push({
            ...item,
            active: true,
            priority: index + 1
          })
        }

        for (const item of toBeInActive) {
          mappedItems.push({
            ...item,
            active: false,
            priority: 9999
          })
        }

        await ExportPriceConversionsService.updateAllItems({
          items: mappedItems
        })

        this.getItems(true)
      } catch (error) {
        console.error('updateAllItems', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading.contentLoading = false
      }
    },
    async updateItem (id) {
      try {
        this.loading.contentLoading = true

        await ExportPriceConversionsService.updateItem(id, this.items.find((item) => item.id === id))

        this.getItems(true)
      } catch (error) {
        console.error('updateItem', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading.contentLoading = false
      }
    },
    showPriceFormat (text) {
      const num = parseFloat(text)

      if (num === 0 || num) {
        return parseFloat(text).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      }

      return '0'
    },
    onTHBInput (text, index) {
      const tmpText = text.replaceAll(',', '')

      if (tmpText !== '' && isNumber(tmpText)) {
        this.items[index].thb = Number(tmpText)
      } else {
        this.items[index].thb = 0
      }
    },
    onValueInput (text, index) {
      const tmpText = text.replaceAll(',', '')

      if (tmpText !== '' && isNumber(tmpText)) {
        this.items[index].value = Number(tmpText)
      } else {
        this.items[index].value = 0
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
.warning-box {
  width: 100%;
  height: 224px;
  border: 2px dashed #BEBEBE;
  border-radius: 0.625rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #9c9c9c;
  gap: 0.3rem;
}
</style>

<style>
/* .search-tag .v-input__slot {
  max-width: unset !important;
  min-height: 28px !important;
  max-height: 28px !important;
} */
.search-tag .v-select__selections input {
  max-height: 28px !important;
}
</style>
